import { Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth.service';
import { OidcService } from './oidc.service';
import { ToasterService } from '@purespectrum1/ui/toaster-service';

@Component({
  selector: 'ps-oidc',
  templateUrl: './oidc.component.html',
  styleUrls: ['./oidc.component.css'],
})
export class OidcComponent implements OnInit {
  public email = new FormControl('', [Validators.email, Validators.required]);
  public isExchanging = false;
  public checkingUser = false;

  private readonly _oidcConfig = environment.pureSpectrum.oidc;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _authService: AuthService,
    private _oidcService: OidcService,
    private _toastr: ToasterService
  ) {}

  get isDisplayWheel(): boolean {
    return this.isExchanging || this.checkingUser;
  }

  ngOnInit() {
    const code = this._route.snapshot.queryParamMap.get('code');
    if (code) {
      this._handleTokenExchange(code);
    }
  }

  /**
   * Identity Discovery
   */
  login() {
    if (this.email.invalid) {
      return;
    }
    this.checkingUser = true;
    const emailValue = this.email.value || '';

    this._oidcService.checkContext(emailValue).subscribe({
      next: (res) => {
        this.checkingUser = false;
        if (res?.valid) {
          this._proceedToOidc();
        } else {
          this._toastr.error(
            'This feature requires authorization. Please contact Customer Service'
          );
        }
      },
      error: (err) => {
        this.checkingUser = false;
        console.error('OIDC Check Error:', err);
        this._toastr.error('Verification service unavailable.');
      },
    });
  }

  /**
   * Redirect to Auth Service
   */
  private _proceedToOidc() {
    const queryParams = new URLSearchParams({
      client_id: this._oidcConfig.clientId,
      response_type: this._oidcConfig.responseType,
      scope: this._oidcConfig.scope,
      redirect_uri: this._oidcConfig.redirectUri,
      login_hint: this.email.value || '',
    });
    window.location.href = `${
      environment.pureSpectrum.authUrl
    }/oidc/auth?${queryParams.toString()}`;
  }

  /**
   * Platform UI Entry
   */
  private _handleTokenExchange(code: string) {
    this.isExchanging = true;

    const payload = {
      code,
      clientId: this._oidcConfig.clientId,
      redirectUri: this._oidcConfig.redirectUri,
    };

    this._oidcService.exchangeCode(payload).subscribe({
      next: (res) => {
        try {
          const sessionData = res.sessionData || res;

          if (sessionData?.token) {
            this._authService.hydrateOidcSession(sessionData);

            setTimeout(() => {
              this._router.navigate(['/dashboard']);
            }, this._oidcConfig.redirectTimeout);
          } else {
            throw new Error('Invalid session payload');
          }
        } catch (err) {
          this._handleExchangeError(err);
        }
      },
      error: (err) => this._handleExchangeError(err),
    });
  }

  private _handleExchangeError(err: any) {
    this.isExchanging = false;
    this._toastr.error('Session synchronization failed.');
    console.error('Error to exchange token: ', err);
  }
}
